import { useState, useEffect } from "react";
import { useTitle } from "../../hooks/useTitle";
import { Link } from "react-router-dom";
import { FileText, ClipboardList, Tag, Clock } from "lucide-react";
import { Query } from "appwrite";
import { tablesDB, DB_ID, ORDERS_TABLE, USERS_TABLE } from "../../appwrite";
import "./Dashboard.css";
import { useStateValue } from "../../StateProvider";

const parseInfoArray = (arr) => {
  if (!Array.isArray(arr)) return {};
  const r = {};
  arr.forEach((item) => {
    if (typeof item === "string") {
      try { Object.assign(r, JSON.parse(item)); }
      catch { const i = item.indexOf(":"); if (i > -1) r[item.slice(0, i).trim()] = item.slice(i + 1).trim(); }
    }
  });
  return r;
};

const fmtNaira = (v) =>
  "₦" +
  parseFloat(v || 0).toLocaleString("en-NG", { minimumFractionDigits: 0 });

const fmtDate = (v) => {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

const PER = 5;

function Pagination({ page, setPage, total }) {
  const pages = Math.ceil(total / PER);
  if (pages <= 1) return null;
  return (
    <div className="db-pagination">
      <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
        ← Prev
      </button>
      <span>
        {page} / {pages}
      </span>
      <button disabled={page === pages} onClick={() => setPage((p) => p + 1)}>
        Next →
      </button>
    </div>
  );
}

export default function Dashboard() {
  useTitle("Seller Dashboard - TrendBay Daily Solutions");
  const [{ user }] = useStateValue();

  const [orderTotal, setOrderTotal] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [productsSold, setProductsSold] = useState(0);
  const [pendingFulfillments, setPendingFulfillments] = useState(0);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [bestsellingProducts, setBestsellingProducts] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [orderPage, setOrderPage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const [customerPage, setCustomerPage] = useState(1);

  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name='theme-color']");
    const originalColor = metaThemeColor?.getAttribute("content");
    if (metaThemeColor) metaThemeColor.setAttribute("content", "#131921");
    return () => {
      if (metaThemeColor) metaThemeColor.setAttribute("content", originalColor);
    };
  }, []);

  useEffect(() => {
    if (!user?.$id) return;
    (async () => {
      try {
        const salesResponse = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: ORDERS_TABLE,
          queries: [Query.orderDesc("$createdAt"), Query.limit(200)],
        });
        const orders = (salesResponse.rows || []).map((row) => ({
          id: row.$id,
          ...row,
        }));

        let totalSales = 0;
        let orderCount = 0;
        let productsSoldCount = 0;
        const pending = [];
        const productMap = {};
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        const recentCustEmails = new Set();

        orders.forEach((order) => {
          const parsedCart = (order.cart || []).map((item) => {
            if (typeof item === "string") { try { return JSON.parse(item); } catch { return {}; } }
            return item;
          });
          const sellerItems = parsedCart.filter((item) => item.sellerId === user.$id);
          if (sellerItems.length === 0) return;

          orderCount++;
          let itemCount = 0;

          sellerItems.forEach((item) => {
            const itemTotal =
              (parseFloat(item.price) || 0) * (item.quantity || 1);
            totalSales += itemTotal;
            productsSoldCount += item.quantity || 0;
            itemCount += item.quantity || 0;
            if (!productMap[item.id]) {
              productMap[item.id] = {
                name: item.title,
                quantity: 0,
                total: 0,
                category: item.category || "N/A",
              };
            }
            productMap[item.id].quantity += item.quantity || 0;
            productMap[item.id].total += itemTotal;
          });

          if (!order.fulfilled) {
            pending.push({
              id: order.id,
              orderNumber: order.orderNumber,
              date: order.$createdAt,
              buyer: parseInfoArray(order.shippingInfo).fullName || "-",
              items: itemCount,
            });
          }

          if (
            new Date(order.$createdAt) >= oneMonthAgo &&
            order.shippingInfo?.email
          ) {
            recentCustEmails.add(order.shippingInfo.email);
          }
        });

        const sortedBest = Object.entries(productMap)
          .map(([id, data]) => ({ id, ...data }))
          .sort((a, b) => b.quantity - a.quantity)
          .slice(0, 20);

        setOrderTotal(totalSales);
        setTotalOrders(orderCount);
        setProductsSold(productsSoldCount);
        setPendingFulfillments(pending.length);
        setPendingOrders(pending);
        setBestsellingProducts(sortedBest);

        // Fetch matching customers
        try {
          const usersRes = await tablesDB.listRows({
            databaseId: DB_ID,
            tableId: USERS_TABLE,
            queries: [Query.limit(500)],
          });
          const allUsers = usersRes.rows || [];
          setCustomers(allUsers.filter((u) => recentCustEmails.has(u.email)));
        } catch {}
      } catch (e) {
        console.error("Error fetching dashboard data:", e);
      }
    })();
  }, [user]);

  const oSlice = pendingOrders.slice((orderPage - 1) * PER, orderPage * PER);
  const pSlice = bestsellingProducts.slice(
    (productPage - 1) * PER,
    productPage * PER,
  );
  const cSlice = customers.slice((customerPage - 1) * PER, customerPage * PER);

  const STAT_CARDS = [
    {
      label: "Total Sales",
      value: fmtNaira(orderTotal),
      icon: FileText,
      color: "#00A855",
      bg: "#f0fdf6",
    },
    {
      label: "Total Orders",
      value: totalOrders,
      icon: ClipboardList,
      color: "#3b82f6",
      bg: "#eff6ff",
    },
    {
      label: "Products Sold",
      value: productsSold,
      icon: Tag,
      color: "#f59e0b",
      bg: "#fffbeb",
    },
    {
      label: "Pending Fulfillments",
      value: pendingFulfillments,
      icon: Clock,
      color: "#8b5cf6",
      bg: "#f5f3ff",
    },
  ];

  return (
    <div className="db-page">
      {/* ── Stat cards ── */}
      <div className="db-stats">
        {STAT_CARDS.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="db-stat-card">
            <div className="db-stat-icon" style={{ background: bg }}>
              <Icon size={20} color={color} />
            </div>
            <div>
              <p className="db-stat-value">{value}</p>
              <p className="db-stat-label">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Pending Orders ── */}
      <div className="db-card">
        <div className="db-card-header">
          <h3 className="db-card-title">Orders Awaiting Fulfillment</h3>
          <Link to="/s/orders" className="db-card-link">
            View all →
          </Link>
        </div>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Order</th>
                <th>Date</th>
                <th>Buyer</th>
                <th>Items</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {oSlice.length === 0 ? (
                <tr>
                  <td colSpan={6} className="db-empty">
                    No pending orders 🎉
                  </td>
                </tr>
              ) : (
                oSlice.map((o, i) => (
                  <tr key={o.id}>
                    <td className="db-td-num">
                      {(orderPage - 1) * PER + i + 1}
                    </td>
                    <td>
                      <Link
                        to={`/s/order-details/${o.id}`}
                        className="db-order-link"
                      >
                        #{o.orderNumber || o.id.slice(-8)}
                      </Link>
                    </td>
                    <td>{fmtDate(o.date)}</td>
                    <td>{o.buyer}</td>
                    <td>{o.items}</td>
                    <td>
                      <span className="db-badge db-badge-pending">Pending</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="db-mobile-cards">
          {oSlice.length === 0 ? (
            <p className="db-empty">No pending orders 🎉</p>
          ) : (
            oSlice.map((o) => (
              <Link
                to={`/s/order-details/${o.id}`}
                className="db-mobile-card"
                key={o.id}
              >
                <div className="db-mc-top">
                  <span className="db-mc-name">
                    #{o.orderNumber || o.id.slice(-8)}
                  </span>
                  <span className="db-badge db-badge-pending">Pending</span>
                </div>
                <div className="db-mc-mid">
                  <span>{o.buyer}</span>
                  <span className="db-mc-items">
                    {o.items} item{o.items !== 1 ? "s" : ""}
                  </span>
                </div>
                <span className="db-mc-date">{fmtDate(o.date)}</span>
              </Link>
            ))
          )}
        </div>
        <Pagination
          page={orderPage}
          setPage={setOrderPage}
          total={pendingOrders.length}
        />
      </div>

      {/* ── Bestselling Products ── */}
      <div className="db-card">
        <div className="db-card-header">
          <h3 className="db-card-title">Your Bestselling Products</h3>
          <Link to="/s/my-vault" className="db-card-link">
            View all →
          </Link>
        </div>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Units Sold</th>
                <th>Revenue</th>
                <th>Category</th>
              </tr>
            </thead>
            <tbody>
              {pSlice.length === 0 ? (
                <tr>
                  <td colSpan={5} className="db-empty">
                    No sales data yet
                  </td>
                </tr>
              ) : (
                pSlice.map((p, i) => (
                  <tr key={p.id}>
                    <td className="db-td-num">
                      {(productPage - 1) * PER + i + 1}
                    </td>
                    <td>{p.name}</td>
                    <td>
                      <strong>{p.quantity}</strong>
                    </td>
                    <td className="db-revenue">{fmtNaira(p.total)}</td>
                    <td>{p.category}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="db-mobile-cards">
          {pSlice.length === 0 ? (
            <p className="db-empty">No sales data yet</p>
          ) : (
            pSlice.map((p) => (
              <div className="db-mobile-card" key={p.id}>
                <div className="db-mc-top">
                  <span className="db-mc-name">{p.name}</span>
                </div>
                <div className="db-mc-mid">
                  <span>{p.quantity} sold</span>
                  <span className="db-revenue">{fmtNaira(p.total)}</span>
                </div>
              </div>
            ))
          )}
        </div>
        <Pagination
          page={productPage}
          setPage={setProductPage}
          total={bestsellingProducts.length}
        />
      </div>

      {/* ── Recent Customers ── */}
      <div className="db-card">
        <div className="db-card-header">
          <h3 className="db-card-title">Your Recent Customers</h3>
        </div>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {cSlice.length === 0 ? (
                <tr>
                  <td colSpan={4} className="db-empty">
                    No customers yet
                  </td>
                </tr>
              ) : (
                cSlice.map((c, i) => (
                  <tr key={c.$id || c.email}>
                    <td className="db-td-num">
                      {(customerPage - 1) * PER + i + 1}
                    </td>
                    <td>{c.fullName || "-"}</td>
                    <td className="db-email">{c.email}</td>
                    <td>{fmtDate(c.$createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="db-mobile-cards">
          {cSlice.length === 0 ? (
            <p className="db-empty">No customers yet</p>
          ) : (
            cSlice.map((c) => (
              <div className="db-mobile-card" key={c.$id || c.email}>
                <div className="db-mc-top">
                  <span className="db-mc-name">{c.fullName || "-"}</span>
                </div>
                <div className="db-mc-mid">
                  <span className="db-email">{c.email}</span>
                  <span className="db-mc-date">{fmtDate(c.$createdAt)}</span>
                </div>
              </div>
            ))
          )}
        </div>
        <Pagination
          page={customerPage}
          setPage={setCustomerPage}
          total={customers.length}
        />
      </div>
    </div>
  );
}
